import { Anchor, FormStatus, P, Pagination } from '@dnb/eufemia';
import { type JSX, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import DataTable, { type Column } from '@/components/DataTable';
import LoadingPage from '@/components/LoadingPage';
import { humanDate } from '@/utils';

/** Minimal type that both v1 ScopeAuditLogDto and v2 ScopeAuditLogResponse satisfy */
interface ScopeAuditLogItem {
  id: string;
  event: string;
  apiId: string;
  appId: string;
  scopeName?: string | null;
  metadata: {
    api?: { name?: string; slug?: string };
    app?: { name?: string };
    scope?: { name?: string };
    scopeRequest?: {
      requestType?: string;
      scopeName?: string;
      reviewStatus?: string;
    };
  };
  createdAt: string;
  updatedAt: string;
}

interface ScopeAuditLogRow {
  id: string;
  event?: string;
  scopeName?: string;
  apiName: string;
  apiSlug: string;
  appName?: string;
  createdAt: string;
  reviewStatus?: string;
}

const COLUMNS: Column<ScopeAuditLogRow>[] = [
  { header: 'Event', attribute: 'event' },
  {
    header: 'Scope',
    attribute: 'scopeName',
    render: ({ scopeName, id }) => (
      <Anchor element={Link} to={`/scopes/history/${id}`}>
        {scopeName}
      </Anchor>
    ),
  },
  { header: 'API', attribute: 'apiName' },
  { header: 'API Slug', attribute: 'apiSlug' },
  { header: 'App', attribute: 'appName' },

  {
    header: 'Created',
    attribute: 'createdAt',
    render: ({ createdAt }) => humanDate(createdAt),
  },
];

const toScopeAuditLogRow = (scopeAuditLogs: ScopeAuditLogItem[]) =>
  scopeAuditLogs.map<ScopeAuditLogRow>((log) => ({
    id: log.id,
    event: log.event,
    scopeName:
      log.metadata.scopeRequest?.requestType === 'CREATE'
        ? log.metadata.scopeRequest?.scopeName
        : log.metadata.scope?.name,
    apiName: log.metadata.api?.name || '',
    apiSlug: log.metadata.api?.slug || '',
    appName: log.metadata.app?.name,
    createdAt: log.createdAt,
    reviewStatus: log.metadata.scopeRequest?.reviewStatus,
  }));
export interface ScopeAuditLogsTableProps {
  scopeAuditLogs: ScopeAuditLogItem[] | null;
  columns?: Array<keyof ScopeAuditLogRow>;
  loading?: boolean;
  currentPage?: number;
  totalPages?: number;
  totalElements?: number;
  onPageChange?: (page: number) => void;
}

export default function ScopeAuditLogsTable({
  scopeAuditLogs,
  columns,
  loading = false,
  currentPage,
  totalPages,
  totalElements,
  onPageChange,
}: ScopeAuditLogsTableProps): JSX.Element {
  const navigate = useNavigate();

  const rows: ScopeAuditLogRow[] = useMemo(() => {
    if (!scopeAuditLogs || scopeAuditLogs.length === 0) {
      return [];
    }
    return toScopeAuditLogRow(scopeAuditLogs);
  }, [scopeAuditLogs]);

  const hasServerPagination = currentPage !== undefined && totalPages !== undefined && onPageChange !== undefined;

  if (loading && !scopeAuditLogs) {
    return <LoadingPage />;
  }
  if (scopeAuditLogs === null) {
    return <FormStatus>Failed to load scope audit logs.</FormStatus>;
  }

  const table = (
    <DataTable
      barContent={<span />} // Just a work around to keep search bar to the right side
      columns={COLUMNS.filter((col) => (columns ? columns.includes(col.attribute) : true))}
      data={rows}
      defaultSortKey="createdAt"
      filterBy={['event', 'scopeName', 'apiName', 'appName', 'createdAt']}
      onShow={({ id }) => navigate(`/scopes/history/${id}`)}
      reverseSort
      {...(hasServerPagination ? { rowsPerPage: rows.length || 20 } : {})}
    />
  );

  if (hasServerPagination && totalPages > 1) {
    return (
      <>
        {totalElements !== undefined && (
          <P bottom="small" weight="medium">
            {totalElements} {totalElements === 1 ? 'audit log' : 'audit logs'}
          </P>
        )}
        <Pagination
          current_page={currentPage + 1}
          on_change={({ pageNumber }) => onPageChange(pageNumber - 1)}
          page_count={totalPages}
        >
          {table}
        </Pagination>
      </>
    );
  }

  return table;
}
